import { BrandMark } from "@/components/brand";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import { motion } from "framer-motion";
import { Room, RoomEvent } from "livekit-client";
import { AlertTriangle, Mic, MicOff, Timer, X } from "lucide-react";
import * as React from "react";
import {
  SessionHeaderContract,
  formatSessionDuration,
  sessionCopy,
} from "../../shared/lesson/view-contracts";
import VoiceAgent from "../VoiceAgent";

interface SessionHeaderProps extends SessionHeaderContract {
  room: Room;
  selectedLanguageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  showLanguageLevel?: boolean;
  getLanguageDisplayName: (code?: string) => string;
  formatSessionTime?: (seconds: number) => string;
  renderRoomAudio?: boolean;
  sessionControls?: React.ReactNode;
}

const LEVEL_LABELS: Record<string, string> = {
  absolute_beginner: "First steps",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

function useLocalMicActivity(room: Room, isActiveSession: boolean, isMuted?: boolean) {
  const [micActivityLevel, setMicActivityLevel] = React.useState(0);

  React.useEffect(() => {
    if (!isActiveSession || isMuted) {
      setMicActivityLevel(0);
      return;
    }

    const updateFromActiveSpeakers = (
      speakers: Array<{ sid?: string; identity?: string; audioLevel?: number }>
    ) => {
      const localParticipant = room.localParticipant;
      const localSpeaker = speakers.find(
        (speaker) =>
          speaker.sid === localParticipant.sid || speaker.identity === localParticipant.identity
      );
      setMicActivityLevel(
        localSpeaker?.audioLevel ? Math.min(1, Math.max(0, localSpeaker.audioLevel)) : 0
      );
    };

    room.on(RoomEvent.ActiveSpeakersChanged, updateFromActiveSpeakers);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, updateFromActiveSpeakers);
    };
  }, [isActiveSession, isMuted, room]);

  return micActivityLevel;
}

export function SessionHeader({
  room,
  isActiveSession,
  sessionDuration,
  selectedLanguageCode,
  selectedLanguageLevel,
  showLanguageLevel = true,
  isMuted,
  onToggleMute,
  onClose,
  getLanguageDisplayName,
  formatSessionTime,
  isZenMode = false,
  assistantDisplayName,
  disableAvatar = false,
  renderRoomAudio = true,
  sessionControls,
}: SessionHeaderProps) {
  const [anamAvatarConnected, setAnamAvatarConnected] = React.useState(false);
  const [anamAvatarError, setAnamAvatarError] = React.useState<string | null>(null);
  const [showAvatar, setShowAvatar] = React.useState(true);
  const micActivityLevel = useLocalMicActivity(room, isActiveSession, isMuted);

  React.useEffect(() => {
    if (isActiveSession) return;
    setAnamAvatarConnected(false);
    setAnamAvatarError(null);
    setShowAvatar(true);
  }, [isActiveSession]);

  React.useEffect(() => {
    if (!disableAvatar) return;
    setShowAvatar(false);
  }, [disableAvatar]);

  const handleCloseAvatar = React.useCallback(async () => {
    setShowAvatar(false);
    setAnamAvatarConnected(false);
    setAnamAvatarError(null);
    try {
      await room.localParticipant.sendText("/avatar-off", { topic: "avatar-control" });
    } catch {}
  }, [room]);

  const resolvedSessionTime = formatSessionTime
    ? formatSessionTime(sessionDuration)
    : formatSessionDuration(sessionDuration);
  const showLanguageBadge = Boolean(
    selectedLanguageCode && selectedLanguageLevel && showLanguageLevel
  );
  const normalizedMicActivity = Math.min(1, Math.max(0, micActivityLevel));
  const [visualMicLevel, setVisualMicLevel] = React.useState(0);

  React.useEffect(() => {
    if (isMuted) {
      setVisualMicLevel(0);
      return;
    }

    const targetLevel = normalizedMicActivity < 0.08 ? 0 : normalizedMicActivity;
    setVisualMicLevel((currentLevel) => {
      const smoothing = targetLevel > currentLevel ? 0.42 : 0.18;
      const nextLevel = currentLevel + (targetLevel - currentLevel) * smoothing;
      return nextLevel < 0.015 ? 0 : Math.min(1, Math.max(0, nextLevel));
    });
  }, [isMuted, normalizedMicActivity]);

  const isHearingUser = !isMuted && visualMicLevel > 0.12;
  const speakingIntensity = isHearingUser ? Math.min(1, (visualMicLevel - 0.12) / 0.58) : 0;
  const micStatusLabel = isMuted ? "Muted" : isHearingUser ? "Hearing you" : "Listening";
  const micButtonScale = isMuted ? 1 : 1 + speakingIntensity * 0.018;
  const micInteractiveScale = isMuted ? 1.02 : 1.02 + speakingIntensity * 0.018;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      data-transcript-header
      className={`z-20 border-b py-3 transition-colors duration-500 ${
        isZenMode ? "bg-white/5 border-white/5" : "border-white/10 bg-[#0f0f14]"
      }`}
    >
      {renderRoomAudio && isActiveSession && (
        <TrackedRoomAudioRenderer
          activeSession={isActiveSession}
          roomName={room.name || null}
          surface="session-header"
        />
      )}

      <div
        data-conversation-column
        className="w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div data-session-toolbar className="flex items-center justify-between">
          <div data-session-identity className="flex items-center gap-3">
            {isZenMode ? (
              <div className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center border border-white/10 shrink-0">
                <div className="w-2 h-2 rounded-full bg-white/50" />
              </div>
            ) : (
              <div className="flex h-9 w-9 shrink-0 select-none items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
                <BrandMark size={36} className="h-full w-full object-contain p-0.5" />
              </div>
            )}

            <motion.div
              initial={{ x: -16, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="leading-tight"
            >
              <div
                className={`text-sm font-bold ${
                  isZenMode ? "font-serif tracking-wide text-white" : "text-white"
                }`}
              >
                {assistantDisplayName || (isZenMode ? "ZenTutor" : sessionCopy.brandAssistantName)}
              </div>
              <div
                data-session-subtitle
                className={`text-[11px] ${isZenMode ? "text-white/35 font-serif" : "text-white/40"}`}
              >
                {isZenMode ? "Meditation coach" : "Real-time conversation"}
              </div>
            </motion.div>

            {isActiveSession && onToggleMute && (
              <div className="flex items-center">
                <motion.button
                  animate={{ scale: micButtonScale }}
                  transition={{ type: "spring", stiffness: 160, damping: 18, mass: 0.45 }}
                  whileFocus={{ scale: micInteractiveScale }}
                  whileHover={{ scale: micInteractiveScale }}
                  whileTap={{ scale: 0.99 }}
                  data-session-mic
                  onClick={onToggleMute}
                  className={`pointer-events-auto relative isolate flex h-10 w-10 shrink-0 origin-center items-center justify-center overflow-visible rounded-md border outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-orange-300/45 ${
                    isMuted
                      ? "border-red-500/35 bg-red-500/15 text-red-300 hover:border-red-400/50 hover:bg-red-500/25"
                      : isZenMode
                        ? "border-white/15 bg-white/[0.07] text-white/80 hover:border-white/25 hover:bg-white/[0.1]"
                        : "border-orange-300/35 bg-orange-400/10 text-orange-200 hover:border-orange-300/55 hover:bg-orange-400/15"
                  }`}
                  aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
                  title={isMuted ? "Unmute microphone" : "Mute microphone"}
                >
                  <span className="sr-only">{micStatusLabel}</span>
                  {isHearingUser && (
                    <motion.span
                      aria-hidden="true"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 0.55, scale: 1.12 }}
                      className={`absolute -inset-1 rounded-md border ${
                        isZenMode ? "border-white/25" : "border-orange-300/45"
                      }`}
                    />
                  )}
                  {isMuted ? (
                    <MicOff className="relative z-10" size={17} />
                  ) : (
                    <Mic
                      className="relative z-10 drop-shadow-[0_0_5px_rgba(255,255,255,0.28)]"
                      size={17}
                    />
                  )}
                </motion.button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isActiveSession && !disableAvatar && showAvatar && (
              <div className="flex items-center gap-2 relative mr-6 sm:mr-8">
                <VoiceAgent
                  room={room}
                  isRecording={isActiveSession}
                  compact={true}
                  requireVideoTrack={true}
                  renderAudioRenderer={false}
                  onAvatarConnectionChange={setAnamAvatarConnected}
                  onAvatarError={setAnamAvatarError}
                />
                {anamAvatarConnected && (
                  <button
                    onClick={handleCloseAvatar}
                    className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-black/70 text-white/85 hover:text-white hover:bg-black/90 flex items-center justify-center transition-colors"
                    aria-label="Close avatar"
                    title="Close avatar"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            )}

            {onClose && (
              <motion.button
                whileTap={{ scale: 0.93 }}
                data-session-end
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white/55 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
                aria-label="End Session"
                title="End Session (Esc)"
              >
                <X size={17} />
              </motion.button>
            )}
          </div>
        </div>

        {isActiveSession && !disableAvatar && showAvatar && anamAvatarError && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-400/25 bg-amber-500/8 px-3 py-2">
            <AlertTriangle size={13} className="text-amber-300 mt-0.5 shrink-0" />
            <span className="text-xs text-amber-100/90">{anamAvatarError}</span>
          </div>
        )}

        {isActiveSession && (
          <div
            data-session-metadata
            className="mt-2.5 flex min-h-7 flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-2.5"
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-white/45">
              <div className="flex items-center gap-1.5">
                <Timer size={12} className="text-orange-300" />
                <span className="font-mono font-semibold tabular-nums text-white/65">
                  {resolvedSessionTime}
                </span>
              </div>

              {showLanguageBadge && (
                <>
                  <span aria-hidden="true" className="text-white/15">
                    /
                  </span>
                  <div className="flex max-w-full min-w-0 items-center gap-1.5">
                    <span className="truncate font-semibold text-white/60">
                      {getLanguageDisplayName(selectedLanguageCode)}
                    </span>
                    <span aria-hidden="true" className="shrink-0 text-white/20">
                      /
                    </span>
                    <span className="shrink-0 text-white/45">
                      {LEVEL_LABELS[selectedLanguageLevel] ?? selectedLanguageLevel}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-3">
              {sessionControls}
              <div data-session-live className="hidden items-center gap-1.5 sm:flex">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-300 opacity-50" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-orange-300" />
                </span>
                <span className="text-[10px] font-semibold uppercase text-white/45">
                  {sessionCopy.liveBadge}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
